import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import {
  Provider,
  ReactReduxContext,
  useDispatch,
  useSelector,
} from 'react-redux';
import * as THREE from 'three';
import * as constantsViewer from './constants';
import { useSpring, a, interpolate } from 'react-spring/three';
import ColladaExporter from './ColladaExporter';
import {
  Canvas,
  extend,
  useFrame,
  useThree,
  useResource,
} from 'react-three-fiber';
import { TrackballControls } from './TrackballControls';
import { OrbitControls, _global_camera_settings } from './OrbitControls';
import { createStructuredSelector } from 'reselect';
import { makeSelectMenuOpen } from 'containers/Ribbon/ShelvesRibbon/selectors';
import {
  makeSelectCabinet,
  makeSelectFurniture,
  makeSelectSelectedIdCabinet,
} from 'containers/Configurator/selectors/furniture';
import * as ConstantsViewer from './constants';
import { Cabinet } from './components/groups/Cabinet';
import { shelvesSelectionAction } from '../Configurator/actions/selection';
import { getCabinetsWithPositions, computeConfiguratorFov } from './helpers';
import { makeSelectActiveTab } from 'containers/Header/selectors';
import { shelvesSelectionMenuAction } from 'containers/Ribbon/ShelvesRibbon/actions';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import saga from './saga';
import { useInjectSaga } from '../../utils/injectSaga';
import { switchTabAction } from '../Header/actions';
import TabNames from '../Header/TabNames';
import { makeSelectActivePanel } from '../Configurator/selectors/general';
import { setColorsPanelAction } from '../Configurator/actions/general';
import { makeSelectConfiguratorMode } from '../ModeBar/selectors';
import { ConfiguratorMode } from 'containers/ModeBar/reducer';
import { makeSelectCursor } from './selectors';
import { cabinetDisplayedAction, furnitureDisplayedAction } from './actions';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

let _global_cabinets_height = 0;
let _global_cabinets_width = 0;
let _global_scene = null;
let _global_in_lookat_animation = false;
let _global_new_lookAt_y = -1;
let _global_lookAt_y = 0;

if (ConstantsViewer.USE_ORBIT_CONTROLS) extend({ OrbitControls });
else extend({ TrackballControls });

const stateProps = createStructuredSelector({
  cabinet: makeSelectCabinet(),
  furniture: makeSelectFurniture(),
  selectedCabinetId: makeSelectSelectedIdCabinet(),
  activeTab: makeSelectActiveTab(),
  menuSelvesSelectorOpen: makeSelectMenuOpen(),
  panelOpen: makeSelectActivePanel(),
  configuratorMode: makeSelectConfiguratorMode(),
  cursor: makeSelectCursor(),
});

function setLookAtYCamera(lookAtY, newLookAtY, duration, iter = -1) {
  if (iter < 0) {
    _global_in_lookat_animation = true;
    setTimeout(function() {
      _global_in_lookat_animation = false;
    }, duration);
  }
  const step = 25;
  iter++;

  _global_lookAt_y =
    lookAtY + ((newLookAtY - lookAtY) / (duration / step)) * (iter + 1);

  if (iter < duration / step && _global_new_lookAt_y === newLookAtY) {
    setTimeout(function() {
      setLookAtYCamera(lookAtY, newLookAtY, duration, iter);
    }, step);
  }
}

function removeDuplicates(array, key) {
  let lookup = new Set();
  return array.filter(obj => !lookup.has(obj[key]) && lookup.add(obj[key]));
}

function removeDuplicates3DObjects(object) {
  if (object && object.children && object.children.length > 0) {
    object.children = removeDuplicates(object.children, 'id');
    for (const ch of object.children) removeDuplicates3DObjects(ch);
  }
}

function Content({ canvasId }) {
  const { setDefaultCamera, scene, aspect, gl } = useThree();
  _global_scene = scene;

  const groupLights = useRef();
  const controls = useRef();
  const lightMain = useRef();
  const lightLeft = useRef();
  const lightRight = useRef();
  const lightBack = useRef();

  const [ref, camera] = useResource();

  // #15929 (https://github.com/mrdoob/three.js/issues/15929)
  // The camera needs to be updated every frame
  // We give this frame a priority so that automatic rendering will be switched off right away
  useFrame(() => {
    //console.log('lk', _global_lookAt_y);

    //camera.lookAt(0, _global_lookAt_y, 0);
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();
    camera.position.set(
      _global_camera_settings.position[0],
      _global_camera_settings.position[1],
      _global_camera_settings.position[2],
    );
  });
  useLayoutEffect(() => void setDefaultCamera(ref.current), []);

  let fov = -1;
  const [lookAtY, setLookAtY] = useState(-1);

  if (_global_camera_settings.fov === undefined)
    _global_camera_settings.fov = 10;
  if (_global_camera_settings.position === undefined)
    _global_camera_settings.position = constantsViewer.DEFAULT_CAMERA_POSITION;

  const [spring, set] = useSpring(() => ({
    position: _global_camera_settings.position,
    fov: _global_camera_settings.fov,
    immediate: true,
    config: { duration: constantsViewer.ANIMATIONS_DURATION_CAMERA },
  }));

  const [furnitureLoaded, setFurnitureLoaded] = useState(false);

  const { cabinet, furniture, selectedCabinetId } = useSelector(stateProps);
  let newLookAtY = lookAtY;
  if (camera && controls && furniture) {
    // Get furniture / cabinet dimensions width ground offset for suspended cabinets
    let height = furniture.heightWithGroundOffset;
    let width = furniture.width;
    if (selectedCabinetId !== null) {
      height = cabinet.height + cabinet.groundOffset;
      width = cabinet.width;
    }
    fov = computeConfiguratorFov(width, height, aspect);
    _global_camera_settings.fov = fov;

    _global_cabinets_height = height;
    _global_cabinets_width = width;
    newLookAtY = 0;

    const humanSize = constantsViewer.CONF_HUMAN_SIZE;
    const fovForHumanSize = constantsViewer.CONF_FOV_RELATIVE_TO_HUMAN_SIZE;
    if (constantsViewer.CONF_HUMAN_SIZE_ACTIVE) {
      newLookAtY = humanSize / 2 - _global_cabinets_height / 2;
      newLookAtY = Math.max(0, newLookAtY);
      fov = Math.max(fov, fovForHumanSize);
      if (lookAtY != newLookAtY) {
        _global_new_lookAt_y = newLookAtY;

        if (lookAtY >= 0 && constantsViewer.ANIMATIONS_ACTIVE_CAMERA)
          setLookAtYCamera(
            lookAtY,
            newLookAtY,
            constantsViewer.ANIMATIONS_DURATION_CAMERA,
          );
        else _global_lookAt_y = newLookAtY;

        setLookAtY(newLookAtY);
      }
    } /*
    // Test for alignement on x axis
    else {
      //fov = Math.max(fov, fovForHumanSize);
      //_global_lookAt_y = 900;
      _global_lookAt_y = _global_cabinets_height / 2;
    }*/
  }

  useEffect(() => {
    set({
      position: constantsViewer.DEFAULT_CAMERA_POSITION,
      immediate: true,
    });
    _global_camera_settings.position = constantsViewer.DEFAULT_CAMERA_POSITION;
    set({
      fov: _global_camera_settings.fov,
      immediate: !furnitureLoaded || !constantsViewer.ANIMATIONS_ACTIVE_CAMERA,
    });
    if (!furnitureLoaded && fov > 0) setFurnitureLoaded(true);
  }, [_global_camera_settings.fov]);

  /*
  useEffect(() => {
    set({
      fov: fov,
      immediate: !furnitureLoaded || !constantsViewer.ANIMATIONS_ACTIVE_CAMERA,
    });
  }, [_global_cabinets_height, _global_cabinets_width]);
*/
  useEffect(() => {
    return () => {
      // gl.forceContextLoss();
      // gl.dispose();
      // scene.dispose();

      gl.state.reset();
    };
  }, []);

  removeDuplicates3DObjects(scene);

  return (
    <>
      <a.perspectiveCamera
        ref={ref}
        fov={spring.fov}
        position={spring.position}
        near={97.55042}
        far={993222.38647}
        up={[0, 1, 0]}
        onUpdate={self => {
          self.lookAt(0, 0, 0);
        }}
      />
      {/* <spotLight
              ref={lightMain}
              color={0xffffff}
              castShadow
              intensity={0.65}
              target-position={[0, 0, 0]}
              position={[0, 0, 2000]}
              shadow-mapSize-width={4000}
              shadow-mapSize-height={4000}
              shadow-camera-near={0.5}
              shadow-camera-far={3000}
              visible
            />
            <spotLight
              ref={lightLeft}
              castShadow
              color={0xffffff}
              intensity={0.5}
              target-position={[0, 0, 0]}
              position={[-2000, 0, 0]}
              shadow-mapSize-width={4000}
              shadow-mapSize-height={4000}
              shadow-camera-near={0.5}
              shadow-camera-far={3000}
            />
            <spotLight
              ref={lightRight}
              castShadow
              color={0xffffff}
              intensity={0.5}
              target-position={[0, 0, 0]}
              position={[2000, 0, 0]}
              shadow-mapSize-width={4000}
              shadow-mapSize-height={4000}
              shadow-camera-near={0.5}
              shadow-camera-far={3000}
            />
            <pointLight
              ref={lightBack}
              castShadow
              color={0xffffff}
              intensity={0.85}
              angle={Math.PI / 2}
              position={[0, 3500, -2000]}
              shadow-mapSize-width={6000}
              shadow-mapSize-height={6000}
              shadow-camera-near={0.5}
              shadow-camera-far={6500}
            /> */}
      <ambientLight color={0xffffff} intensity={0.1} />
      <group ref={groupLights}>
        <pointLight
          color={0x9f9f9f}
          intensity={0.23}
          position={[0, 9000, 9910.71191]}
        />
        <pointLight
          color={0x9f9f9f}
          intensity={0.23}
          position={[0, 9000, -9910.71191]}
        />
        <pointLight
          color={0x9f9f9f}
          position={[7927.87695, 8741.13037, 10910.71191]}
        />
        <pointLight
          color={0xa09f9f}
          intensity={0.5}
          position={[7108.82178, -9983.94531, 6746.88574]}
        />
        <pointLight
          color={0xa09f9f}
          position={[-7927.82178, 9983.94531, -10746.88574]}
        />
        <pointLight
          color={0xa09f9f}
          intensity={0.5}
          position={[-7108.82178, -9983.94531, -6746.88574]}
        />
      </group>
      {camera && groupLights && ConstantsViewer.USE_ORBIT_CONTROLS ? (
        <orbitControls
          ref={controls}
          args={[
            camera,
            document.getElementById(canvasId),
            set,
            groupLights.current,
          ]}
        />
      ) : camera ? (
        <trackballControls
          ref={controls}
          args={[camera, document.getElementById(canvasId)]}
        />
      ) : null}

      {furniture &&
        getCabinetsWithPositions(
          furniture,
          selectedCabinetId,
        ).map(positionCabinet => (
          <Cabinet
            key={positionCabinet.cabinet.id}
            multiCabinets={selectedCabinetId === null}
            cabinet={positionCabinet.cabinet}
            position={positionCabinet.position}
          />
        ))}
    </>
  );
}

const Viewer = () => {
  useInjectReducer({ key: 'viewer', reducer: reducer });
  useInjectSaga({ key: 'viewer', saga: saga });

  const {
    selectedCabinetId,
    activeTab,
    menuSelvesSelectorOpen,
    panelOpen,
    configuratorMode,
    cursor,
  } = useSelector(stateProps);

  const dispatch = useDispatch();

  const isCabinetInFurniture = selectedCabinetId !== null;

  useEffect(() => {
    if (isCabinetInFurniture) dispatch(cabinetDisplayedAction());
    else dispatch(furnitureDisplayedAction());
  }, [isCabinetInFurniture]);

  const canvasId = `canvasConfigurator`;

  return (
    <ReactReduxContext.Consumer>
      {({ store }) => (
        <Canvas
          gl={canvas => new THREE.WebGLRenderer({ canvas })}
          onCreated={({ gl, scene }) => {
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = THREE.PCFSoftShadowMap;
            /*var axes = new THREE.AxisHelper(2500);
            scene.add(axes);*/
            /* var camHelper = new THREE.CameraHelper(
              lightMain.current.shadow.camera,
            );
            // scene.add(camHelper);
            var camHelperLeft = new THREE.CameraHelper(
              lightLeft.current.shadow.camera,
            );
            //scene.add(camHelperLeft);
            var camHelperRight = new THREE.CameraHelper(
              lightRight.current.shadow.camera,
            );
            //scene.add(camHelperRight);
            var camHelperBack = new THREE.CameraHelper(
              lightBack.current.shadow.camera,
            );
            //scene.add(camHelperBack); */
          }}
          onPointerMissed={e => {
            if (
              selectedCabinetId !== null &&
              activeTab === 'shelves' &&
              configuratorMode === ConfiguratorMode.selector
            ) {
              if (menuSelvesSelectorOpen)
                dispatch(shelvesSelectionMenuAction(false));
              else if (panelOpen === undefined) {
                dispatch(switchTabAction(TabNames.cabinet));
              } else {
                dispatch(setColorsPanelAction());
              }
            }
          }}
          id={canvasId}
          className={`mode-${cursor}`}
        >
          <Provider store={store}>
            <Content canvasId={canvasId} />
          </Provider>
        </Canvas>
      )}
    </ReactReduxContext.Consumer>
  );
};

function save(blob, filename) {
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

function saveString(text, filename) {
  save(new Blob([text], { type: 'text/plain' }), filename);
}

function saveArrayBuffer(buffer, filename) {
  save(new Blob([buffer], { type: 'application/octet-stream' }), filename);
}

const link = document.createElement('a');
link.style.display = 'none';
document.body.appendChild(link);

export function getColladaExport() {
  const scene = _global_scene;

  // Instantiate an exporter
  const exporter = new ColladaExporter();
  const result = exporter.parse(scene);

  return result;
}

export function exportCollada(reference = '') {
  const scene = _global_scene;
  let filename = 'kewlox';
  if (reference !== '') filename += `-${reference}`;

  // Instantiate an exporter
  const exporter = new ColladaExporter();
  const result = exporter.parse(scene);

  const zip = new JSZip();
  zip.file(`${filename}.dae`, result.data);
  result.textures.forEach(tex =>
    zip.file(`textures/${tex.name}.${tex.ext}`, tex.data),
  );
  zip
    .generateAsync({ type: 'blob', compression: 'DEFLATE' })
    .then(function(content) {
      saveAs(content, `${filename}.zip`);
    });
}

export function getPNGimage(width = 1000, height = 1000, pov = null) {
  const povImage = pov ? pov : constantsViewer.POV_LEFT;
  const scene = _global_scene;
  const rendererExport = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  rendererExport.setSize(width, height);

  let fov = computeConfiguratorFov(
    _global_cabinets_width,
    _global_cabinets_height,
    width / height,
  );
  const humanSize = 1750;
  const fovForHumanSize = 22;
  let lookAtY = humanSize / 2 - _global_cabinets_height / 2;
  lookAtY = Math.max(0, lookAtY);
  fov = Math.max(fov, fovForHumanSize);

  const camera_obj = new THREE.PerspectiveCamera(
    fov,
    1,
    597.55042,
    993222.38647,
  );
  camera_obj.position.x = povImage[0];
  camera_obj.position.y = povImage[1];
  camera_obj.position.z = povImage[2];
  camera_obj.up = new THREE.Vector3(0, 1, 0);
  const target = new THREE.Vector3(0.0, lookAtY, 0.0);
  camera_obj.lookAt(target);
  rendererExport.render(scene, camera_obj);

  return rendererExport.domElement.toDataURL('image/png');
}

window.getPNGimage = getPNGimage;
window.exportCollada = exportCollada;

export default Viewer;
